import { CheckOutlined, SearchOutlined } from '@ant-design/icons'
import React, { CSSProperties, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useHistory } from 'react-router-dom'
import { useAuth } from '../../contexts/Auth'

interface FormData {
  selectedCategory: number | null
  selectedSubcategories: number[]
  otherActivitiesChecked: boolean
  otherActivityTypeNames: string[]
  selectedExperience: string
  uploadedLicense: string | null
  [key: string]: any
}

interface StepProps {
  onNext: () => void
  onPrevious: () => void
  onStepProgress: (progress: number) => void
  formData: FormData
  updateFormData: (data: Partial<FormData>) => void
}

interface Category {
  id: number
  name: string
  description: string
  icon: string
  subcategories: Subcategory[]
}

interface Subcategory {
  id: number
  name: string
  description: string
  icon: string
}

const Step1: React.FC<StepProps> = ({
  onNext,
  onPrevious,
  onStepProgress,
  formData,
  updateFormData,
}) => {
  const [categories, setCategories] = useState<Category[]>([])
  const [loading, setLoading] = useState(true)
  const [uploading, setUploading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [uploadError, setUploadError] = useState<string | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const { userLoggedIn } = useAuth()
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const history = useHistory()

  const experiences = [
    { id: '1-2 years', label: '1-2 years' },
    { id: '3-4 years', label: '3-4 years' },
    { id: '5+ years', label: '5+ years' },
  ]

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        setLoading(true)
        const response = await fetch(
          `${process.env.REACT_APP_TS_RESTFUL_API_URL}/activity/categories`,
        )
        if (!response.ok) {
          throw new Error('Network response was not ok')
        }
        const data = await response.json()
        if (data.result && data.data) {
          const uniqueCategories = data.data.map((category: Category) => {
            const uniqueSubcategories = Array.from(
              new Map(category.subcategories.map((sub) => [sub.id, sub])).values(),
            )
            return { ...category, subcategories: uniqueSubcategories }
          })
          setCategories(uniqueCategories)
        } else {
          throw new Error(data.message || 'Failed to fetch categories')
        }
      } catch (err: any) {
        setError(err.message || 'An error occurred')
      } finally {
        setLoading(false)
      }
    }

    fetchCategories()
  }, [])

  const calculateProgress = useCallback(() => {
    let completedFields = 0
    const totalRequiredFields = 3

    const hasSubcategories = formData.selectedSubcategories.length > 0
    const hasOtherActivities = formData.otherActivityTypeNames.length > 0

    if (formData.selectedCategory !== null) completedFields += 1
    if (formData.selectedExperience) completedFields += 1
    if (hasSubcategories || hasOtherActivities) completedFields += 1

    const progress = (completedFields / totalRequiredFields) * 100

    return Math.min(progress, 100)
  }, [
    formData.selectedCategory,
    formData.selectedExperience,
    formData.selectedSubcategories,
    formData.otherActivityTypeNames,
  ])

  useEffect(() => {
    const progress = calculateProgress()
    onStepProgress(progress)
  }, [calculateProgress, onStepProgress])

  const handleCategorySelect = (categoryId: number) => {
    if (formData.selectedCategory === categoryId) {
      return
    }

    updateFormData({
      selectedCategory: categoryId,
      selectedSubcategories: [],
      otherActivityTypeNames: [],
    })
  }

  const handleSubcategorySelect = (subcategoryId: number) => {
    const currentSubcategories = formData.selectedSubcategories || []
    const newSubcategories = currentSubcategories.includes(subcategoryId)
      ? currentSubcategories.filter((id) => id !== subcategoryId)
      : [...currentSubcategories, subcategoryId]

    updateFormData({ selectedSubcategories: newSubcategories })
  }

  const handleAddOtherActivity = (activityName: string) => {
    const name = activityName.trim()
    if (name) {
      const isAlreadyAdded = formData.otherActivityTypeNames.some(
        (existingName) => existingName.toLowerCase() === name.toLowerCase(),
      )
      const isSubcategory = allSubcategories.some(
        (sub) => sub.name.toLowerCase() === name.toLowerCase(),
      )

      if (!isAlreadyAdded && !isSubcategory) {
        updateFormData({
          otherActivityTypeNames: [...formData.otherActivityTypeNames, name],
          otherActivitiesChecked: true,
        })
      }
    }
    setSearchQuery('')
  }

  const handleRemoveOtherActivity = (nameToRemove: string) => {
    const newNames = formData.otherActivityTypeNames.filter((name) => name !== nameToRemove)
    updateFormData({
      otherActivityTypeNames: newNames,
      otherActivitiesChecked: newNames.length > 0,
    })
  }

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (!file) {
      return
    }

    if (!userLoggedIn?.userToken) {
      setUploadError('Authentication token is missing. Please log in again.')
      return
    }

    const allowedTypes = ['image/jpeg', 'image/png']
    const maxFileSize = 20 * 1024 * 1024
    if (!allowedTypes.includes(file.type)) {
      setUploadError('Invalid file type. Please upload a JPEG or PNG image.')
      return
    }
    if (file.size > maxFileSize) {
      setUploadError('File size exceeds the 20MB limit.')
      return
    }

    setUploading(true)
    setUploadError(null)

    const formDataUpload = new FormData()
    formDataUpload.append('fileName', file)
    formDataUpload.append('deviceType', 'WEBSITE')
    formDataUpload.append('project', 'cms')

    try {
      const response = await fetch(`${process.env.REACT_APP_TS_UPLOAD_URL}/upload`, {
        method: 'POST',
        headers: {
          Authorization: userLoggedIn.userToken,
          project: 'haup-stable',
        },
        body: formDataUpload,
      })

      if (!response.ok) {
        throw new Error('Upload failed')
      }

      const result = await response.json()
      if (result.result && result.fileUrl) {
        updateFormData({ uploadedLicense: result.fileUrl })
      } else {
        throw new Error(result.message || 'Upload failed')
      }
    } catch (err: any) {
      setUploadError(err.message || 'An error occurred during upload')
      updateFormData({ uploadedLicense: null })
    } finally {
      setUploading(false)
    }
  }

  const handleLicenseClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click()
    }
  }

  const handleRemovePhoto = () => {
    updateFormData({ uploadedLicense: null })
    setUploadError(null)
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }

  const handleRemoveTag = (e: React.MouseEvent, subcategoryId: number) => {
    e.stopPropagation()
    handleSubcategorySelect(subcategoryId)
  }

  const handleBackButtonClick = () => {
    history.push('/submission#activities')
  }

  const canProceed =
    formData.selectedCategory !== null &&
    formData.selectedExperience !== '' &&
    (formData.selectedSubcategories.length > 0 || formData.otherActivityTypeNames.length > 0)

  const selectedCategoryData = useMemo(() => {
    return categories.find((cat) => cat.id === formData.selectedCategory)
  }, [categories, formData.selectedCategory])

  const openModal = () => {
    if (formData.selectedCategory) {
      setIsModalOpen(true)
    }
  }

  const handleModalClose = () => {
    setIsModalOpen(false)
    setSearchQuery('')
  }

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value)
  }

  const allSubcategories = selectedCategoryData?.subcategories || []
  const filteredSubcategories = allSubcategories.filter((sub) =>
    sub.name.toLowerCase().includes(searchQuery.toLowerCase()),
  )

  const isNewCategory =
    searchQuery.trim() !== '' &&
    !allSubcategories.some((sub) => sub.name.toLowerCase() === searchQuery.toLowerCase()) &&
    !formData.otherActivityTypeNames.some(
      (name) => name.toLowerCase() === searchQuery.toLowerCase(),
    )

  const allSelectedItems = [
    ...(formData.selectedSubcategories || []).map((id) => {
      const sub = allSubcategories.find((s) => s.id === id)
      return sub ? { id: sub.id, name: sub.name, type: 'subcategory' } : null
    }),
    ...(formData.otherActivityTypeNames || []).map((name) => ({
      id: name,
      name: name,
      type: 'other',
    })),
  ].filter(Boolean) as { id: number | string; name: string; type: string }[]

  const styles: { [key: string]: CSSProperties } = {
    container: {
      backgroundColor: '#fff',
      padding: '16px',
      boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
      width: '100%',
      maxWidth: '500px',
      margin: '0 auto',
      boxSizing: 'border-box',
      fontFamily: 'Poppins, sans-serif',
    },
    label: {
      display: 'block',
      fontSize: '16px',
      color: '#212427',
      marginBottom: '8px',
      fontWeight: '500',
      fontFamily: 'Poppins, sans-serif',
    },
    input: {
      width: '100%',
      padding: '12px 16px',
      border: '1px solid #D6DAE0',
      borderRadius: '8px',
      fontSize: '16px',
      boxSizing: 'border-box',
      fontFamily: 'Poppins, sans-serif',
    },
    buttonPrimary: {
      padding: '12px 24px',
      backgroundColor: '#0048B3',
      color: 'white',
      border: 'none',
      borderRadius: '24px',
      cursor: 'pointer',
      fontWeight: '500',
      fontSize: '16px',
      fontFamily: 'Poppins, sans-serif',
    },
    buttonSecondary: {
      padding: '12px 24px',
      backgroundColor: 'transparent',
      color: '#667085',
      border: '1px solid #D6DAE0',
      borderRadius: '24px',
      cursor: 'pointer',
      fontWeight: '500',
      fontSize: '16px',
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      fontFamily: 'Poppins, sans-serif',
    },
    modalOverlay: {
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
      padding: '16px',
      boxSizing: 'border-box',
    },
    modalContent: {
      backgroundColor: 'white',
      borderRadius: '12px',
      width: '100%',
      maxWidth: '490px',
      maxHeight: 'calc(100vh - 32px)',
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden',
      fontFamily: 'Poppins, sans-serif',
      position: 'relative',
      willChange: 'transform',
      margin: 'auto',
    },
    modalHeader: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      padding: '16px',
      borderBottom: '1px solid #E5E7EB',
      flexShrink: 0,
    },
    modalTitle: {
      margin: 0,
      fontWeight: '600',
      fontSize: '18px',
      color: '#212427',
      fontFamily: 'Poppins, sans-serif',
      flex: 1,
      minWidth: 0,
    },
    modalCloseBtn: {
      background: 'none',
      border: 'none',
      fontSize: '24px',
      cursor: 'pointer',
      color: '#667085',
      padding: '0',
    },
    modalSearchInputContainer: {
      margin: '16px',
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      position: 'relative',
      isolation: 'isolate',
    },
    modalSearchInput: {
      width: '100%',
      border: '1px solid #D6DAE0',
      borderRadius: '8px',
      padding: '12px 16px',
      fontSize: '16px',
      boxSizing: 'border-box',
      fontFamily: 'Poppins, sans-serif',
      paddingLeft: '40px',
      paddingRight: '60px',
      outline: 'none',
      WebkitAppearance: 'none',
      WebkitTapHighlightColor: 'transparent',
      minWidth: 0,
    },
    searchIcon: {
      position: 'absolute',
      left: '12px',
      top: '50%',
      transform: 'translateY(-50%)',
      color: '#667085',
    },
    modalList: {
      listStyle: 'none',
      margin: 0,
      padding: '0 16px',
      overflowY: 'auto',
      flex: 1,
      minHeight: 0,
    },
    modalListItem: {
      padding: '12px 0',
      cursor: 'pointer',
      fontSize: '14px',
      fontWeight: '400',
      fontFamily: 'Poppins, sans-serif',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      borderBottom: '1px solid #E5E7EB',
    },
    tag: {
      backgroundColor: '#EBF4FF',
      color: '#0048B3',
      border: 'none',
      borderRadius: '20px',
      padding: '6px 12px',
      fontSize: '14px',
      fontWeight: '500',
      margin: '0',
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
    },
    tagCloseButton: {
      background: 'none',
      border: 'none',
      color: '#0048B3',
      cursor: 'pointer',
      fontSize: '14px',
      padding: '0',
    },
    addButton: {},
  }

  const filteredAndSortedItems = useMemo(() => {
    const combinedList = [
      ...allSubcategories.map((sub) => ({ ...sub, type: 'subcategory' })),
      ...formData.otherActivityTypeNames.map((name) => ({ id: name, name, type: 'other' })),
    ]

    const filteredList = combinedList.filter((item) =>
      item.name.toLowerCase().includes(searchQuery.toLowerCase()),
    )

    const sortedList = filteredList.sort((a, b) => {
      if (a.type === 'other' && b.type !== 'other') return -1
      if (a.type !== 'other' && b.type === 'other') return 1
      return a.name.localeCompare(b.name)
    })

    return sortedList
  }, [searchQuery, allSubcategories, formData.otherActivityTypeNames])

  return (
    <div style={styles.container}>
      {/* Header */}
      <div style={{ marginBottom: '24px' }}>
        <h2
          style={{
            fontSize: '20px',
            fontWeight: '600',
            color: '#212427',
            margin: '0',
            fontFamily: 'Poppins, sans-serif',
          }}
        >
          Choose activity category
        </h2>
      </div>

      {/* Categories */}
      <div style={{ marginBottom: '24px' }}>
        <label
          style={{
            display: 'block',
            fontSize: '16px',
            color: '#212427',
            marginBottom: '16px',
            fontWeight: '500',
            fontFamily: 'Poppins, sans-serif',
          }}
        >
          Choose relevant category for your activity <span style={{ color: '#ef4444' }}>*</span>
        </label>

        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(3, 1fr)',
            gap: '12px',
            marginBottom: '20px',
          }}
        >
          {categories.map((category) => (
            <div
              key={category.id}
              onClick={() => handleCategorySelect(category.id)}
              style={{
                padding: '16px 12px',
                border:
                  formData.selectedCategory === category.id
                    ? '2px solid #0048B3'
                    : '2px solid #D6DAE0',
                borderRadius: '8px',
                textAlign: 'center',
                cursor: 'pointer',
                backgroundColor: formData.selectedCategory === category.id ? '#EFF6FF' : '#fff',
                transition: 'all 0.2s ease',
              }}
            >
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  marginBottom: '8px',
                  height: '40px',
                }}
              >
                <img
                  src={category.icon}
                  alt={category.name}
                  style={{
                    width: '40px',
                    height: '40px',
                    objectFit: 'contain',
                  }}
                />
              </div>
              <div
                style={{
                  fontSize: '13px',
                  fontWeight: '500',
                  color: formData.selectedCategory === category.id ? '#0048B3' : '#212427',
                  lineHeight: '1.3',
                  fontFamily: 'Poppins, sans-serif',
                }}
              >
                {category.name}
              </div>
            </div>
          ))}
        </div>

        {/* Selected Categories Input (Combined) */}
        <div style={{ marginBottom: '24px' }}>
          <label
            style={{
              display: 'block',
              fontSize: '16px',
              color: '#212427',
              marginBottom: '8px',
              fontWeight: '500',
              fontFamily: 'Poppins, sans-serif',
            }}
          >
            Add related category <span style={{ color: '#ef4444' }}>*</span>
          </label>
          <div
            onClick={openModal}
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              alignItems: 'center',
              gap: '8px',
              border: '1px solid #D6DAE0',
              borderRadius: '8px',
              padding: '8px',
              cursor: formData.selectedCategory ? 'pointer' : 'not-allowed',
              backgroundColor: formData.selectedCategory ? '#fff' : '#F9FAFB',
              minHeight: '48px',
            }}
          >
            {allSelectedItems.length > 0 ? (
              allSelectedItems.map((item) => (
                <div key={item.id} style={styles.tag}>
                  <span>{item.name}</span>
                  <button
                    onClick={(e) => {
                      e.stopPropagation()
                      if (item.type === 'subcategory') {
                        handleSubcategorySelect(item.id as number)
                      } else {
                        handleRemoveOtherActivity(item.name)
                      }
                    }}
                    style={styles.tagCloseButton}
                  >
                    &times;
                  </button>
                </div>
              ))
            ) : (
              <span style={{ color: '#667085', padding: '4px' }}>Choose category</span>
            )}
          </div>
        </div>
      </div>

      {/* Experience */}
      <div style={{ marginBottom: '24px' }}>
        <label
          style={{
            display: 'block',
            fontSize: '16px',
            color: '#212427',
            marginBottom: '16px',
            fontWeight: '500',
            fontFamily: 'Poppins, sans-serif',
          }}
        >
          Years of experience for this activity category <span style={{ color: '#ef4444' }}>*</span>
        </label>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          {experiences.map((exp) => (
            <label
              key={exp.id}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                cursor: 'pointer',
                fontSize: '16px',
                color: '#212427',
                fontFamily: 'Poppins, sans-serif',
              }}
            >
              <input
                type="radio"
                name="experience"
                value={exp.id}
                checked={formData.selectedExperience === exp.id}
                onChange={(e) => updateFormData({ selectedExperience: e.target.value })}
                style={{
                  width: '18px',
                  height: '18px',
                  accentColor: '#0048B3',
                }}
              />
              {exp.label}
            </label>
          ))}
        </div>
      </div>

      {/* License Upload */}
      <div style={{ marginBottom: '24px' }}>
        <label
          style={{
            display: 'block',
            fontSize: '16px',
            color: '#212427',
            marginBottom: '16px',
            fontWeight: '500',
            fontFamily: 'Poppins, sans-serif',
          }}
        >
          Tour guide license or certification <span style={{ color: '#667085' }}>(optional)</span>
        </label>

        <input
          type="file"
          ref={fileInputRef}
          onChange={handleFileChange}
          accept=".jpg,.jpeg,.png"
          style={{ display: 'none' }}
        />

        {formData.uploadedLicense ? (
          <div
            style={{
              border: '2px solid #10B981',
              borderRadius: '8px',
              padding: '16px',
              backgroundColor: '#F0FDF4',
              display: 'flex',
              alignItems: 'center',
              gap: '16px',
            }}
          >
            <img
              src={formData.uploadedLicense}
              alt="Uploaded license"
              style={{ width: '60px', height: '60px', objectFit: 'cover', borderRadius: '4px' }}
            />
            <div style={{ flex: 1 }}>
              <p
                style={{
                  color: '#10B981',
                  fontWeight: '500',
                  fontSize: '14px',
                  fontFamily: 'Poppins, sans-serif',
                  margin: '0',
                }}
              >
                License uploaded
              </p>
              <a
                href={formData.uploadedLicense}
                target="_blank"
                rel="noopener noreferrer"
                style={{ color: '#0048B3', fontSize: '12px', textDecoration: 'underline' }}
              >
                View photo
              </a>
            </div>
            <button
              onClick={handleRemovePhoto}
              style={{
                background: 'none',
                border: 'none',
                color: '#667085',
                cursor: 'pointer',
                fontSize: '20px',
                padding: '4px',
                lineHeight: '1',
              }}
              disabled={uploading}
            >
              &times;
            </button>
          </div>
        ) : (
          <div
            onClick={handleLicenseClick}
            style={{
              border: '2px dashed #D6DAE0',
              borderRadius: '8px',
              padding: '24px',
              textAlign: 'center',
              backgroundColor: '#F9FAFB',
              cursor: 'pointer',
              transition: 'all 0.2s ease',
            }}
          >
            <div style={{ fontSize: '32px', marginBottom: '12px' }}>{uploading ? '⏳' : '📷'}</div>
            <p
              style={{
                color: '#0048B3',
                fontWeight: '500',
                fontSize: '14px',
                fontFamily: 'Poppins, sans-serif',
                margin: '0',
              }}
            >
              {uploading ? 'Uploading...' : 'Upload or take a photo'}
            </p>
            {uploadError && (
              <p style={{ color: '#ef4444', fontSize: '12px', marginTop: '8px', margin: '0' }}>
                {uploadError}
              </p>
            )}
          </div>
        )}
      </div>

      {/* Navigation */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <button onClick={handleBackButtonClick} style={styles.buttonSecondary}>
          ← Back
        </button>

        <button
          onClick={onNext}
          disabled={!canProceed || uploading}
          style={{
            ...styles.buttonPrimary,
            backgroundColor: canProceed && !uploading ? '#0048B3' : '#D6DAE0',
            cursor: canProceed && !uploading ? 'pointer' : 'not-allowed',
          }}
        >
          Next
        </button>
      </div>

      {/* Custom Subcategory Modal */}
      {isModalOpen && (
        <div style={styles.modalOverlay}>
          <div style={styles.modalContent}>
            <div style={styles.modalHeader}>
              <h3 style={styles.modalTitle}>Add related category</h3>
              <button onClick={handleModalClose} style={styles.modalCloseBtn}>
                &times;
              </button>
            </div>

            <div style={styles.modalSearchInputContainer}>
              <SearchOutlined style={styles.searchIcon} />
              <input
                type="text"
                placeholder="Search or add a new category"
                value={searchQuery}
                onChange={handleSearchChange}
                style={styles.modalSearchInput}
                onKeyPress={(e) => {
                  if (e.key === 'Enter' && isNewCategory) {
                    handleAddOtherActivity(searchQuery)
                  }
                }}
              />
              {isNewCategory && (
                <button
                  onClick={() => handleAddOtherActivity(searchQuery)}
                  style={{
                    position: 'absolute',
                    right: '12px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    backgroundColor: 'transparent',
                    border: 'none',
                    color: '#0048B3',
                    fontWeight: '600',
                    cursor: 'pointer',
                    padding: '4px 8px',
                    fontSize: '14px',
                    zIndex: 10,
                    pointerEvents: 'auto',
                  }}
                >
                  Add
                </button>
              )}
            </div>

            <ul style={styles.modalList}>
              {/* Combine and sort all categories to display custom ones first */}
              {filteredAndSortedItems.map((item) => (
                <li
                  key={item.id}
                  onClick={() => {
                    if (item.type === 'subcategory') {
                      handleSubcategorySelect(item.id as number)
                    } else {
                      handleRemoveOtherActivity(item.name)
                    }
                  }}
                  style={{
                    ...styles.modalListItem,
                    color:
                      (item.type === 'subcategory' &&
                        (formData.selectedSubcategories || []).includes(item.id as number)) ||
                      (item.type === 'other' &&
                        (formData.otherActivityTypeNames || []).includes(item.name))
                        ? '#0048B3'
                        : '#212427',
                  }}
                >
                  <span>
                    {item.name}
                    {item.type === 'other' && ' (Custom)'}
                  </span>
                  {((item.type === 'subcategory' &&
                    (formData.selectedSubcategories || []).includes(item.id as number)) ||
                    (item.type === 'other' &&
                      (formData.otherActivityTypeNames || []).includes(item.name))) && (
                    <CheckOutlined style={{ color: '#0048B3' }} />
                  )}
                </li>
              ))}

              {/* Show a message if no items are found */}
              {filteredAndSortedItems.length === 0 && !isNewCategory && (
                <p style={{ color: '#667085', margin: 0, padding: '16px 0', textAlign: 'center' }}>
                  No categories found.
                </p>
              )}
            </ul>

            <div
              style={{
                display: 'flex',
                justifyContent: 'flex-end',
                padding: '16px',
                borderTop: '1px solid #E5E7EB',
                flexShrink: 0,
              }}
            >
              <button
                onClick={handleModalClose}
                style={{
                  ...styles.buttonPrimary,
                  padding: '10px 20px',
                }}
              >
                Done
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default Step1
